import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CONTACT_EMAIL } from "@/services/inquiry";
import { pages } from "../content/pages";
import { useContent, Rich, SlotImg, HashLink, useReveal, SlotInspector } from "./helpers";
import { createThreads } from "../home/platformModelEngine";

/** Wraps every page: design system, gentle scroll reveal and the ?slots inspector. */
export function PageShell({ children, testId }) {
  const root = useRef(null);
  useReveal(root);
  return (
    <div className="kss-v2 k-page" ref={root} data-testid={testId}>
      {children}
      <SlotInspector rootRef={root} />
    </div>
  );
}

/** Photo header used on inner pages: breadcrumb, title, lead and an "On this page" list. */
export function PageHero({ hero }) {
  const c = useContent(pages).common;
  return (
    <section className={`phero${hero.media ? "" : " no-media"}`}>
      {hero.media && <SlotImg slot={hero.media} className="phero-bg" eager decorative sizes="100vw" />}
      <div className="k-wrap phero-in">
        <div className="phero-top">
          <nav className="crumbs" aria-label={c.breadcrumb}><Link to="/">{c.home}</Link><span aria-hidden="true">/</span><span aria-current="page">{hero.crumb}</span></nav>
          {hero.eyebrow && <p className="k-label">{hero.eyebrow}</p>}
          <h1 className="phero-title"><Rich text={hero.title} /></h1>
        </div>
        <p className="phero-lead"><Rich text={hero.lead} /></p>
        {hero.onpage?.length > 0 && (
          <nav className="onpage" aria-label={c.onThisPage}>
            <p className="k-label">{c.onThisPage}</p>
            <ol>{hero.onpage.map(([id, label]) => <li key={id}><HashLink href={`#${id}`}>{label}</HashLink></li>)}</ol>
          </nav>
        )}
      </div>
    </section>
  );
}

/**
 * Closing "contact" band (all pages). Compact heading + actions on the left, a "next step"
 * ticket on the right in the style of the KS-101 station terminal, joined by a running stitch.
 * Text: cta.{eyebrow,title,body,button}; ticket steps come from pages.contact.next.
 */
export function CtaSignOff({ cta, id = "cta" }) {
  const all = useContent(pages);
  const c = all.common;
  const k = all.contact;
  const canvas = useRef(null);
  const ticket = useRef(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => createThreads(canvas.current), []);
  useEffect(() => {
    const el = ticket.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => el.classList.toggle("run", e.isIntersecting), { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(CONTACT_EMAIL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); }).catch(() => {});
  };
  return (
    <section className="signoff" id={id} aria-labelledby={`${id}-title`}>
      <canvas ref={canvas} aria-hidden="true" />
      <div className="k-wrap signoff-in">
        <div className="signoff-head rv">
          <p className="k-label">{cta.eyebrow || c.contactUs}</p>
          <h2 id={`${id}-title`}><Rich text={cta.title} /></h2>
        </div>
        <div className="signoff-copy rv">
          <p className="signoff-body"><Rich text={cta.body} /></p>
          <div className="signoff-actions">
            <Link className="k-btn k-btn-mint" to="/contact">{cta.button || c.contactUs} <span className="ar" aria-hidden="true">→</span></Link>
            <div className="mail"><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a><button type="button" onClick={copy} aria-live="polite">{copied ? c.copied : c.copy}</button></div>
          </div>
        </div>
        <div className="sticket rv" ref={ticket}>
          <div className="sticket-top"><span>{k.nextLabel}</span><span className="led" aria-hidden="true" /></div>
          <ol>
            {k.next.map(([t, d], i) => (
              <li key={t}><b aria-hidden="true">{String(i + 1).padStart(2, "0")}</b><div><strong>{t}</strong><span>{d}</span></div></li>
            ))}
          </ol>
          <p className="sticket-foot">{k.reply}</p>
        </div>
      </div>
    </section>
  );
}

/** "Available now" / "In development" label with a symbol, never colour alone. */
export function StatusChip({ status }) {
  const c = useContent(pages).common;
  const now = status === "current" || status === "available";
  return <span className={`chip ${now ? "now" : status === "demo" ? "demo" : "plan"}`}><i aria-hidden="true" />{now ? c.current : status === "demo" ? c.demo : c.planned}</span>;
}

/** Adds the class "in" once the element scrolls into view (for one-off animations). */
export function useInViewClass(ref, threshold = 0.3) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { el.classList.add("in"); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add("in"); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);
}
